import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Box,
  Tooltip,
} from "@mui/material";
import { Edit, Delete, Add, Sync } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import ReactPaginate from "react-paginate";
import queryString from "query-string";
import Api from "../../utils/api";

const HeaderBannerList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = queryString.parse(location.search);

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTime, setSearchTime] = useState("");

  // State container for pagination params
  const [params, setParams] = useState({
    page: query.page && !isNaN(query.page) ? parseInt(query.page) : 1,
    limit: query.limit && !isNaN(query.limit) ? parseInt(query.limit) : 20,
    page_filter: query.page_filter ? query.page_filter : "",
    text: query.text ? query.text : "",
  });

  useEffect(() => {
    fetchBanners();
  }, [params.page, params.limit, params.page_filter, params.text]);

  useEffect(() => {
    let n_params = params;
    const keys = Object.keys(query);

    for (let i = 0; i < keys.length; i++) {
      switch (keys[i]) {
        case "page":
          if (n_params.page !== parseInt(query.page)) {
            n_params.page = parseInt(query.page);
          }
          break;
        case "limit":
          if (n_params.limit !== parseInt(query.limit)) {
            n_params.limit = parseInt(query.limit);
          }
          break;
        case "page_filter":
          if (n_params.page_filter !== query.page_filter) {
            n_params.page_filter = query.page_filter;
          }
          break;
        case "text":
          if (n_params.text !== query.text) {
            n_params.text = query.text;
          }
          break;
        default:
          break;
      }
    }

    setParams({ ...n_params });
  }, [query.page, query.limit, query.page_filter, query.text]);

  const fetchBanners = () => {
    setLoading(true);

    const queryParams = new URLSearchParams({
      page: params.page,
      limit: params.limit,
      ...(params.page_filter && { page: params.page_filter }),
      ...(params.text && { text: params.text }),
    });

    new Api()
      .get(`/header-banner?${queryParams.toString()}`)
      .then((response) => {
        if (response && response.status) {
          // Handle the nested response structure
          const responseData = response.details || response.data;
          console.log(responseData);
          setBanners(responseData.data || []);
          setTotalPages(responseData.last_page || 1);
          setTotalItems(responseData.total || 0);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleDelete = (id) => {
    new Api().delete(`/header-banner/destroy/${id}`).then((response) => {
      if (response && response.status) {
        fetchBanners();
        setDeleteDialog({ open: false, id: null });
      }
    });
  };

  const handleChangeSearchInput = (event, useTimeout = false) => {
    const { name, value } = event.target;

    if (useTimeout) {
      if (searchTime) {
        clearTimeout(searchTime);
      }
      const loop = setTimeout(() => {
        _makeQueryStringParams(name, value);
      }, 500);
      setSearchTime(loop);
    } else {
      _makeQueryStringParams(name, value);
    }
  };

  const _makeQueryStringParams = (type, value) => {
    let n_params = { ...params };
    n_params.page = 1; // Reset to first page when filtering

    switch (type) {
      case "page":
        n_params.page = parseInt(value);
        break;
      case "limit":
        n_params.limit = parseInt(value);
        break;
      case "page_filter":
        n_params.page_filter = value;
        break;
      case "text":
        n_params.text = value;
        break;
      default:
        break;
    }

    // Create URL
    let url = "?";
    url += `page=${n_params.page}`;
    url += `&limit=${n_params.limit}`;
    if (n_params.page_filter) url += `&page_filter=${n_params.page_filter}`;
    if (n_params.text) url += `&text=${n_params.text}`;

    navigate(`/header-banner${url}`);
  };

  const getPageTitle = (page) => {
    const titles = {
      product: "محصولات",
      blog: "بلاگ",
      landing: "صفحه اصلی",
    };
    return titles[page] || page;
  };

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Typography variant="h4">مدیریت بنر هدر</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/header-banner/create")}
        >
          افزودن بنر جدید
        </Button>
      </div>

      {/* Search Filters */}
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              label="فیلتر بر اساس صفحه"
              value={params.page_filter}
              variant="outlined"
              margin="dense"
              fullWidth
              name="page_filter"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) => handleChangeSearchInput(event, false)}
            >
              <MenuItem value="">همه صفحات</MenuItem>
              <MenuItem value="product">محصولات</MenuItem>
              <MenuItem value="blog">بلاگ</MenuItem>
              <MenuItem value="landing">صفحه اصلی</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="جستجو در متن"
              variant="outlined"
              margin="dense"
              fullWidth
              name="text"
              value={params.text}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) => handleChangeSearchInput(event, true)}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Tooltip title="بروزرسانی" onClick={() => fetchBanners()}>
              <IconButton>
                <Sync />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Pagination Controls */}
      <Box mb={2}>
        <Grid container alignItems="center">
          <Grid item xs={4} sm={6}>
            <TextField
              select
              value={params.limit}
              margin="dense"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) =>
                _makeQueryStringParams("limit", event.target.value)
              }
            >
              <MenuItem value="10">10</MenuItem>
              <MenuItem value="20">20</MenuItem>
              <MenuItem value="30">30</MenuItem>
              <MenuItem value="50">50</MenuItem>
              <MenuItem value="100">100</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={8} sm={6}>
            <ReactPaginate
              previousLabel={"<"}
              nextLabel={">"}
              breakLabel={"..."}
              pageCount={totalPages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={(selectedItem) =>
                _makeQueryStringParams("page", selectedItem.selected + 1)
              }
              containerClassName={"pagination"}
              activeClassName={"active"}
              forcePage={params.page - 1}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Table Display */}
      <div style={{ overflowX: "auto" }}>
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>صفحه</th>
              <th>متن بنر</th>
              <th>لینک</th>
              <th>چشمک زن</th>
              <th>تاریخ ایجاد</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {banners.map((banner) => (
              <tr key={banner.id}>
                <td>{banner.id}</td>
                <td>{getPageTitle(banner.page)}</td>
                <td style={{ maxWidth: "200px", wordBreak: "break-word" }}>
                  {banner.text.length > 50
                    ? banner.text.substring(0, 50) + "..."
                    : banner.text}
                </td>
                <td style={{ maxWidth: "150px", wordBreak: "break-all" }}>
                  {banner.banner_link ? (
                    <a
                      href={banner.banner_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {banner.banner_link.length > 30
                        ? banner.banner_link.substring(0, 30) + "..."
                        : banner.banner_link}
                    </a>
                  ) : (
                    "ندارد"
                  )}
                </td>
                <td>{banner.blinked === "1" ? "بله" : "خیر"}</td>
                <td style={{ direction: "ltr" }}>
                  {new Date(banner.created_at).toLocaleDateString("fa-IR")}
                </td>
                <td>
                  <Tooltip title="ویرایش">
                    <IconButton
                      color="primary"
                      onClick={() =>
                        navigate(`/header-banner/edit/${banner.id}`)
                      }
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="حذف">
                    <IconButton
                      color="secondary"
                      onClick={() =>
                        setDeleteDialog({ open: true, id: banner.id })
                      }
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom Pagination */}
      <Box mt={3}>
        <ReactPaginate
          previousLabel={"<"}
          nextLabel={">"}
          breakLabel={"..."}
          pageCount={totalPages}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={(selectedItem) =>
            _makeQueryStringParams("page", selectedItem.selected + 1)
          }
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={params.page - 1}
        />
      </Box>

      {/* Total Items Display */}
      <Box mt={2}>
        <Typography variant="body2" color="textSecondary">
          مجموع: {totalItems} بنر
        </Typography>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>تأیید حذف</DialogTitle>
        <DialogContent>
          <Typography>آیا از حذف این بنر اطمینان دارید؟</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            انصراف
          </Button>
          <Button
            color="secondary"
            onClick={() => handleDelete(deleteDialog.id)}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      <style jsx>{`
        @keyframes blink {
          0%,
          50% {
            opacity: 1;
          }
          51%,
          100% {
            opacity: 0.5;
          }
        }
      `}</style>

      {loading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.8)"
          zIndex={9999}
        >
          <Typography>در حال بارگذاری...</Typography>
        </Box>
      )}
    </div>
  );
};

export default HeaderBannerList;
